import { SESSION_STORAGE_KEYS } from 'constant/sessionStorage';
import { VARICON_DOMAIN } from 'constant/globals';

/**
 * The two decisions that gate browser tracing: WHO traces (internal-user check)
 * and HOW MUCH of their traffic is kept (head-sample ratio).
 *
 * These live here rather than in `lib/instrumentation.ts` so they can be tested
 * as pure functions — importing `instrumentation.ts` boots the whole OTel SDK
 * (providers, exporters, timers) as an import side effect, which is not
 * something a unit test can pull in. Same reasoning as `otelIngest.ts`.
 */

/**
 * Head-sample ratio for `TraceIdRatioBasedSampler`, parsed from config.
 *
 * Clamped to [0, 1] and defaulted to 1 for anything unparseable, because the
 * sampler treats out-of-range input as "keep everything" anyway — better to
 * make the clamp explicit than to hand the SDK a value it silently reinterprets.
 * A missing/blank env var therefore means "no head sampling", NOT "traces off":
 * the off switch is an unset `OTEL_COLLECTOR_URL`.
 *
 * The blank check is load-bearing, not defensive. Vite yields `''` (not
 * `undefined`) for a key that is present but empty — which is how this repo's
 * `.env.sample` ships optional keys — so the config-level `?? '1'` does NOT
 * catch it. And `Number('')` is `0`, so without this guard a blank
 * `VITE_APP_OTEL_TRACE_SAMPLE_RATIO=` would head-sample at 0 and silently drop
 * EVERY span while looking correctly configured.
 */
export function parseSampleRatio(value: string): number {
  if (value.trim() === '') {
    return 1;
  }
  // Catches NaN and ±Infinity alike: unparseable config falls back to 1 rather
  // than to a value the sampler would reinterpret on its own.
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return 1;
  }
  return Math.min(1, Math.max(0, n));
}

/**
 * True when `email` belongs to Varicon staff — the dogfood cohort that gets
 * tracing before the `ENABLE_TRACING` flag promotes it to everyone.
 *
 * Suffix match on the domain, case-insensitive because the value comes from
 * whatever the user typed at login.
 */
export const isInternalEmail = (email: string | null | undefined): boolean =>
  (email ?? '').toLowerCase().endsWith(VARICON_DOMAIN);

/**
 * Reads the signed-in user's email from sessionStorage (not the React auth
 * context — instrumentation initialises before the app mounts) and applies
 * {@link isInternalEmail}.
 */
export const isInternalUser = (): boolean =>
  isInternalEmail(sessionStorage.getItem(SESSION_STORAGE_KEYS.EMAIL));
